
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, Calendar, CreditCard, AlertTriangle } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const SubscriptionStatus = () => {
  const { subscription, isSubscribed, cancelSubscription } = useSubscription();
  const navigate = useNavigate();

  if (!subscription) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Subscription Status
          </CardTitle>
          <CardDescription>
            You don't have an active subscription
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => navigate('/pricing')} className="bg-[#FF8C00] hover:bg-orange-600">
            Upgrade to Premium
          </Button>
        </CardContent>
      </Card>
    );
  }

  const subscriptionEnd = new Date(subscription.subscription_end);
  const daysRemaining = Math.ceil((subscriptionEnd.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Crown className="h-5 w-5 text-[#FF8C00]" />
          Subscription Status
        </CardTitle>
        <CardDescription>
          Manage your premium subscription
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="font-medium">Plan:</span>
          <Badge className="bg-[#FF8C00] text-white">
            {subscription.subscription_tier} Premium
          </Badge>
        </div>

        <div className="flex items-center justify-between">
          <span className="font-medium">Status:</span>
          <Badge variant={isSubscribed ? 'default' : 'destructive'}>
            {isSubscribed ? 'Active' : 'Expired'}
          </Badge>
        </div>

        <div className="flex items-center justify-between">
          <span className="font-medium">Expires:</span>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">
              {format(subscriptionEnd, 'MMM dd, yyyy')}
            </span>
          </div>
        </div>

        {isSubscribed && daysRemaining <= 7 && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <span className="text-sm text-amber-700">
              Your subscription expires in {daysRemaining} days
            </span>
          </div>
        )}

        <div className="flex gap-2 pt-4">
          {isSubscribed ? (
            <Button
              variant="outline"
              onClick={cancelSubscription}
              className="border-red-500 text-red-500 hover:bg-red-50"
            >
              Cancel Subscription
            </Button>
          ) : (
            <Button onClick={() => navigate('/pricing')} className="bg-[#FF8C00] hover:bg-orange-600">
              Renew Subscription
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SubscriptionStatus;
